import { motion } from "framer-motion";
import {
  Lightbulb,
  Hammer,
  LayoutGrid,
  Sparkles,
  CircuitBoard,
} from "lucide-react";
import SceneShell from "../components/SceneShell";

/**
 * Scene 58 — The Journey · Concept → Fully Connected AI Suite.
 *
 * An animated horizontal timeline:
 *  • A glowing line draws left-to-right across the stage.
 *  • Five milestone nodes pulse in as the line passes each one.
 *  • The final node lights up with a gold glow and a small "today" anchor.
 *
 * Sits as the bridge between Section 1's pivot ("And yet…") and the start
 * of Section 2 (Core Packages). Used to land "we've gone from concept to a
 * fully connected AI product suite — already delivering value at build stage"
 * before any of the product detail.
 */

const GOLD = "#e9a820";
const GOLD_DEEP = "#d4940a";
const LIME = "#96C11F";

const MILESTONES = [
  {
    icon: Lightbulb,
    label: "Concept",
    sub: "An idea on paper",
    color: "rgba(255,255,255,0.55)",
  },
  {
    icon: Hammer,
    label: "Foundation",
    sub: "Pipelines · capture · infrastructure",
    color: LIME,
  },
  {
    icon: LayoutGrid,
    label: "Core Stack",
    sub: "Social Go · Social Pro · live",
    color: LIME,
  },
  {
    icon: Sparkles,
    label: "New Products",
    sub: "PresenTour · NarraTour + capture studio",
    color: GOLD,
  },
  {
    icon: CircuitBoard,
    label: "Fully Connected AI Suite",
    sub: "Already delivering value",
    color: GOLD,
    final: true,
  },
];

export default function Scene58_ConnectedSuite() {
  // ── timing baseline (seconds)
  const lineDuration = 4.4;
  const nodeStaggerStart = 0.4;
  const nodeStaggerStep = (lineDuration - nodeStaggerStart) / (MILESTONES.length - 1);

  return (
    <SceneShell variant="ink" align="center" data-testid="scene-connected-suite">
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          background: [
            "radial-gradient(ellipse at 30% 50%, rgba(150,193,31,0.10) 0%, transparent 55%)",
            "radial-gradient(ellipse at 70% 50%, rgba(233,168,32,0.18) 0%, transparent 55%)",
            "radial-gradient(ellipse at 30% 50%, rgba(150,193,31,0.10) 0%, transparent 55%)",
          ],
        }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="relative max-w-[1700px] mx-auto w-full flex flex-col">
        <motion.h2
          initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.0, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="kn-display text-3xl sm:text-4xl lg:text-5xl text-white text-center mt-2 mb-16"
          data-testid="journey-title"
        >
          From <span className="text-white/55">concept</span>{" "}
          <span className="text-white/55">→</span> a{" "}
          <span
            style={{
              background: `linear-gradient(135deg, ${GOLD_DEEP} 0%, ${GOLD} 50%, ${GOLD_DEEP} 100%)`,
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              fontWeight: 800,
            }}
          >
            fully connected AI suite.
          </span>
        </motion.h2>

        {/* ── THE TIMELINE ──────────────────────────────────────────── */}
        <div className="relative w-full px-6 sm:px-12 lg:px-20 py-8">
          {/* 1. Background rail */}
          <div
            className="absolute top-1/2 left-6 right-6 sm:left-12 sm:right-12 lg:left-20 lg:right-20 h-px"
            style={{
              background:
                "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.18) 8%, rgba(255,255,255,0.18) 92%, rgba(255,255,255,0) 100%)",
              translate: "0 -0.5px",
            }}
          />

          {/* 2. The glowing line that DRAWS left → right */}
          <motion.div
            className="absolute top-1/2 left-6 sm:left-12 lg:left-20 h-[3px] origin-left rounded-full"
            style={{
              right: "1.5rem",
              background: `linear-gradient(90deg, ${LIME} 0%, ${LIME} 40%, ${GOLD} 75%, ${GOLD} 100%)`,
              boxShadow: `0 0 20px ${GOLD}80, 0 0 40px ${LIME}55`,
              translate: "0 -1.5px",
            }}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{
              duration: lineDuration,
              delay: 0.6,
              ease: [0.16, 1, 0.3, 1],
            }}
            data-testid="journey-line"
          />

          {/* 3. The leading-edge spark — a bright dot that travels with the line head */}
          <motion.div
            className="absolute top-1/2 w-4 h-4 rounded-full pointer-events-none"
            style={{
              left: "1.5rem",
              translate: "-50% -50%",
              background: GOLD,
              boxShadow: `0 0 30px ${GOLD}, 0 0 60px ${GOLD}b3`,
            }}
            initial={{ x: 0, opacity: 0 }}
            animate={{
              x: ["0%", "calc(100vw - 200px)"],
              opacity: [0, 1, 1, 0],
            }}
            transition={{
              duration: lineDuration,
              delay: 0.6,
              times: [0, 0.05, 0.95, 1],
              ease: [0.16, 1, 0.3, 1],
            }}
          />

          {/* 4. Milestones */}
          <div className="relative flex justify-between items-stretch">
            {MILESTONES.map((m, i) => {
              const Icon = m.icon;
              const t = nodeStaggerStart + i * nodeStaggerStep + 0.6; // start delay
              const isAbove = i % 2 === 0;
              return (
                <div
                  key={m.label}
                  className="relative flex-1 flex flex-col items-center"
                  data-testid={`journey-node-${i}`}
                >
                  {/* Above-rail label (alternates with below) */}
                  {isAbove && (
                    <NodeContent milestone={m} delay={t + 0.25} above />
                  )}

                  {/* The node circle */}
                  <motion.div
                    initial={{ scale: 0.4, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{
                      delay: t,
                      duration: 0.7,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                    className="relative z-10 my-12"
                  >
                    <span
                      className="absolute inset-0 rounded-full pointer-events-none"
                      style={{
                        boxShadow: `0 0 0 6px ${m.color}26, 0 0 24px ${m.color}66`,
                      }}
                    />
                    <span
                      className="block w-12 h-12 rounded-full flex items-center justify-center"
                      style={{
                        background: `radial-gradient(circle at 30% 30%, ${m.color}80 0%, ${m.color}26 70%)`,
                        border: `2px solid ${m.color}`,
                        backdropFilter: "blur(6px)",
                      }}
                    >
                      <Icon
                        className="w-5 h-5"
                        style={{
                          color: m.final
                            ? "#fff"
                            : i === 0
                            ? "rgba(255,255,255,0.85)"
                            : m.color,
                        }}
                      />
                    </span>

                    {/* Pulsing ring on the FINAL node */}
                    {m.final && (
                      <>
                        {[0, 1].map((r) => (
                          <motion.span
                            key={r}
                            className="absolute inset-0 rounded-full pointer-events-none"
                            style={{
                              border: `1.5px solid ${GOLD}`,
                            }}
                            animate={{ scale: [1, 2.6], opacity: [0.7, 0] }}
                            transition={{
                              duration: 2.4,
                              repeat: Infinity,
                              delay: t + 0.3 + r * 1.0,
                              ease: "easeOut",
                            }}
                          />
                        ))}
                      </>
                    )}
                  </motion.div>

                  {/* Below-rail label (alternates) */}
                  {!isAbove && (
                    <NodeContent milestone={m} delay={t + 0.25} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Final lockup — appears after the timeline finishes */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: lineDuration + 0.8, duration: 0.9 }}
          className="text-center mt-12 flex flex-col items-center gap-4"
          data-testid="journey-final-lockup"
        >
          <div
            className="inline-flex items-center gap-3 rounded-full px-7 py-3"
            style={{
              background: "rgba(233,168,32,0.10)",
              border: `1px solid ${GOLD}73`,
              boxShadow: `0 0 40px ${GOLD}33`,
            }}
          >
            <span
              className="w-2 h-2 rounded-full"
              style={{ background: GOLD, boxShadow: `0 0 12px ${GOLD}` }}
            />
            <span
              className="text-sm sm:text-base tracking-[0.18em] uppercase"
              style={{ color: "rgba(255,255,255,0.92)", fontWeight: 700 }}
            >
              Q1 · so far
            </span>
          </div>

          {/* Tech surface — what's powering the suite under the hood */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            {[
              { label: "25+ APIs" },
              { label: "Multiple LLMs" },
              { label: "Multiple AI tools" },
            ].map((t, i) => (
              <motion.span
                key={t.label}
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  delay: lineDuration + 1.2 + i * 0.18,
                  duration: 0.6,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[11px] tracking-[0.18em] uppercase"
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.15)",
                  color: "rgba(255,255,255,0.85)",
                  fontWeight: 700,
                }}
                data-testid={`journey-tech-${i}`}
              >
                <span
                  className="w-1 h-1 rounded-full"
                  style={{ background: i === 0 ? LIME : GOLD }}
                />
                {t.label}
              </motion.span>
            ))}
          </div>
        </motion.div>
      </div>
    </SceneShell>
  );
}

function NodeContent({ milestone, delay, above = false }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: above ? -8 : 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`absolute ${above ? "bottom-[calc(50%+34px)]" : "top-[calc(50%+34px)]"} left-1/2 -translate-x-1/2 text-center w-[160px] sm:w-[180px]`}
    >
      <div
        className="kn-display text-base sm:text-lg leading-tight"
        style={{
          color: milestone.final ? "#fff" : milestone.color,
          fontWeight: milestone.final ? 800 : 700,
          textShadow: milestone.final
            ? `0 0 18px ${milestone.color}80`
            : "none",
        }}
      >
        {milestone.label}
      </div>
      <div
        className="text-[11px] sm:text-xs mt-1.5"
        style={{ color: "rgba(255,255,255,0.55)", lineHeight: 1.35 }}
      >
        {milestone.sub}
      </div>
    </motion.div>
  );
}
